import { useCallback, useEffect, useState } from "react";
import {
  fetchInfoScreenEventsConfig,
  syncInfoScreenEvents,
  testEventbriteConnection,
  updateInfoScreenEventsConfig,
  updateInfoScreenEventsSecrets,
} from "@/lib/infoScreenEventsApi";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import type { InfoScreenEventsConfig, InfoScreenSyncResult } from "@/types/infoScreenEvents";

type EventsSettings = Record<string, unknown>;

interface InfoScreenProvidersPanelProps {
  canWrite: boolean;
}

export function InfoScreenProvidersPanel({ canWrite }: InfoScreenProvidersPanelProps) {
  const [config, setConfig] = useState<InfoScreenEventsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<InfoScreenSyncResult | null>(null);
  const [ebTesting, setEbTesting] = useState(false);
  const [ebTestResult, setEbTestResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [ebToken, setEbToken] = useState("");
  const [ebOrgId, setEbOrgId] = useState("");
  const [tmKey, setTmKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await fetchInfoScreenEventsConfig();
      setConfig(cfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const eventsSettings = (config?.events ?? {}) as EventsSettings;
  const providers = (eventsSettings.providers ?? {}) as Record<string, { enabled?: boolean }>;

  function patchEvents(patch: EventsSettings) {
    if (!config) return;
    setConfig({
      ...config,
      events: { ...eventsSettings, ...patch },
    });
    setSuccess(false);
  }

  function patchProvider(id: string, enabled: boolean) {
    patchEvents({
      providers: {
        ...providers,
        [id]: { ...(providers[id] ?? {}), enabled },
      },
    });
  }

  async function onSaveConfig() {
    if (!config || !canWrite) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await updateInfoScreenEventsConfig({
        events: config.events,
        modules: config.modules,
      });
      setConfig(saved);
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function onSaveSecrets() {
    if (!canWrite) return;
    setSaving(true);
    try {
      const saved = await updateInfoScreenEventsSecrets({
        eventbrite_token: ebToken || null,
        eventbrite_organization_id: ebOrgId || null,
        ticketmaster_api_key: tmKey || null,
      });
      setConfig(saved);
      setEbToken("");
      setEbOrgId("");
      setTmKey("");
      setSuccess(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "API-Schlüssel speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function onSync() {
    if (!canWrite) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncInfoScreenEvents();
      setSyncResult(result);
      setSuccess(true);
      const failed = Object.entries(result.providers).filter(([, p]) => !p.success);
      if (failed.length > 0) {
        setError(failed.map(([id, p]) => `${id}: ${p.error ?? "Fehler"}`).join(" · "));
      } else {
        setError(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Synchronisation fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  }

  async function onTestEventbrite() {
    if (!canWrite) return;
    setEbTesting(true);
    setEbTestResult(null);
    try {
      const result = await testEventbriteConnection();
      setEbTestResult(result.message);
      if (!result.success) {
        setError(result.message);
      } else {
        setError(null);
      }
    } catch (e) {
      setEbTestResult(e instanceof Error ? e.message : "Test fehlgeschlagen");
      setError(e instanceof Error ? e.message : "Test fehlgeschlagen");
    } finally {
      setEbTesting(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  if (!config) {
    return <p className="text-sm text-status-red">{error ?? "Konfiguration nicht verfügbar"}</p>;
  }

  return (
    <div className="space-y-6">
      <Card title="Datenquellen">
        <div className="space-y-3">
          {[
            { id: "manual", label: "Eigene Veranstaltungen" },
            { id: "eventbrite", label: "Eventbrite" },
            { id: "ticketmaster", label: "Ticketmaster Discovery" },
            { id: "eventim", label: "Eventim (öffentliche API, kein Schlüssel)" },
          ].map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                disabled={!canWrite}
                checked={Boolean(providers[p.id]?.enabled)}
                onChange={(e) => patchProvider(p.id, e.target.checked)}
              />
              {p.label}
            </label>
          ))}
          {canWrite ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => void onSync()} disabled={syncing}>
              {syncing ? "Synchronisiere…" : "Externe Quellen jetzt aktualisieren"}
            </Button>
          ) : null}
          {syncResult ? (
            <ul className="mt-2 space-y-1 text-sm">
              {Object.entries(syncResult.providers).map(([id, p]) => (
                <li key={id} className={p.success ? "text-emerald-700" : "text-red-600"}>
                  {id}: {p.success ? `${p.count} Events` : p.error ?? "Fehler"}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Card>

      <Card title="API-Schlüssel (nur Server)">
        <p className="mb-3 text-sm text-label-secondary">
          Schlüssel werden verschlüsselt in den Mandanten-Einstellungen gespeichert und nie im
          Frontend angezeigt. Eventbrite liefert nur noch Events Ihrer eigenen Organisation.
        </p>
        <div className="space-y-3 text-sm">
          <p>
            Eventbrite:{" "}
            {config.secrets_status.eventbrite_configured
              ? `hinterlegt (${config.secrets_status.eventbrite_masked})`
              : "nicht konfiguriert"}
            {config.secrets_status.eventbrite_organization_id
              ? ` · Org-ID ${config.secrets_status.eventbrite_organization_id}`
              : ""}
          </p>
          <p>
            Ticketmaster:{" "}
            {config.secrets_status.ticketmaster_configured
              ? `hinterlegt (${config.secrets_status.ticketmaster_masked})`
              : "nicht konfiguriert"}
          </p>
          {canWrite ? (
            <>
              <input
                type="password"
                className="eh-input w-full max-w-md"
                placeholder="Neuer Eventbrite-Token"
                value={ebToken}
                onChange={(e) => setEbToken(e.target.value)}
                autoComplete="off"
              />
              <input
                type="text"
                className="eh-input w-full max-w-md"
                placeholder="Eventbrite-Organisations-ID (optional, sonst Benutzer-ID)"
                value={ebOrgId}
                onChange={(e) => setEbOrgId(e.target.value)}
                autoComplete="off"
              />
              <input
                type="password"
                className="eh-input w-full max-w-md"
                placeholder="Neuer Ticketmaster API-Key"
                value={tmKey}
                onChange={(e) => setTmKey(e.target.value)}
                autoComplete="off"
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => void onSaveSecrets()} disabled={saving}>
                  API-Schlüssel speichern
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => void onTestEventbrite()}
                  disabled={ebTesting || !config.secrets_status.eventbrite_configured}
                >
                  {ebTesting ? "Teste Eventbrite…" : "Eventbrite-Verbindung testen"}
                </Button>
              </div>
              {ebTestResult ? (
                <p className={ebTestResult.includes("OK") ? "text-emerald-700" : "text-red-600"}>
                  {ebTestResult}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      </Card>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Gespeichert.</p> : null}

      {canWrite ? (
        <Button type="button" onClick={() => void onSaveConfig()} disabled={saving}>
          {saving ? "Speichern…" : "Datenquellen-Einstellungen speichern"}
        </Button>
      ) : null}
    </div>
  );
}
